import type { UseMutationResult } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import { useCallback, useMemo, useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import type { TranslationKeys } from "../../../i18n/types";
import type { WageRulesConfigResponse, WageSettingsResponse } from "../../../types/wageRules";
import type { NurseRole } from "../../../utils/models";
import { useSingleFlightSave } from "./useSingleFlightSave";
import type { WageSettingsSection } from "./useWageRulesConfig";

/** How one wage rule table is read, validated and sent. The three sections differ only in this. */
export type WageRulesDraftSpec<TRow extends { name: string }> = {
	section: Exclude<WageSettingsSection, "training_orientation">;
	/** This table's stored rows in the tab's own read ([] = "all" markers, as the API returns them). */
	stored: (settings: WageSettingsResponse) => TRow[];
	/** Stored shape → the table's shape (explicit full selections instead of []). */
	fromApi: (stored: TRow[] | null | undefined, roles: NurseRole[]) => TRow[];
	/** The table's shape → the request body. Throws, with a readable message, on a row the backend would reject. */
	forBackend: (rows: TRow[], roles: NurseRole[], jobIds: string[] | undefined) => TRow[];
	/** Save-blocking problems, as translation keys, shown in the Save tooltip. */
	reasons: (rows: TRow[], roles: NurseRole[]) => TranslationKeys[];
};

type WageRulesSaveMutation<TRow> = UseMutationResult<WageRulesConfigResponse<TRow>, Error, TRow[]>;

/**
 * DAT-905 · One wage rule section's draft: seeded from the tab's own read (`useWageSettings`), dirty
 * against it, saved on its own (one request per Save, never two in flight), and re-seeded from what the server stored.
 *
 * `settings` is `undefined` until THIS department's read lands — never `chosenDepartment`'s rows, which
 * are `[]` for any department but the token's. Mounted under a per-department `key` by the Team page,
 * so switching departments re-seeds from fresh state, and our own save writes the stored rows into the
 * read's cache.
 */
export const useWageRulesDraft = <TRow extends { name: string }>(
	spec: WageRulesDraftSpec<TRow>,
	settings: WageSettingsResponse | undefined,
	save: WageRulesSaveMutation<TRow>,
) => {
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { trackError } = useAnalytics();
	const { roles, jobIds, department_id: departmentId } = chosenDepartment;
	const storedRows = settings === undefined ? undefined : spec.stored(settings);

	// `undefined` until the department's rows arrive. Saving before then would send a table that does
	// not include them, which REPLACES every stored rule with whatever was typed meanwhile.
	const rowsLoaded = storedRows !== undefined;
	const savedRows = useMemo(() => spec.fromApi(storedRows, roles), [spec, storedRows, roles]);
	// `baseline` is what the draft was last seeded from. While the draft still matches it (untouched),
	// a newer `savedRows` re-seeds both: the rows arriving after mount must not leave an empty draft.
	// Adjusted during render rather than in an effect, so no frame paints the stale draft.
	const [baseline, setBaseline] = useState<TRow[]>(savedRows);
	const [rows, setRows] = useState<TRow[]>(savedRows);
	if (!isEqual(baseline, savedRows) && isEqual(rows, baseline)) {
		setBaseline(savedRows);
		setRows(savedRows);
	}
	const reseed = useCallback((next: TRow[]) => {
		setBaseline(next);
		setRows(next);
	}, []);

	const isDirty = !isEqual(rows, savedRows);
	const reasons = useMemo(() => spec.reasons(rows, roles), [spec, rows, roles]);

	const buildBody = useCallback((): TRow[] | undefined => {
		try {
			return spec.forBackend(rows, roles, jobIds);
		} catch (error) {
			if (!(error instanceof Error)) throw error;
			trackError("team_wage_settings_validation_error", { department_id: departmentId, section: spec.section, error: error.message });
			showErrorSnackbar(error.message, {}, error);
			return undefined;
		}
	}, [departmentId, jobIds, roles, rows, showErrorSnackbar, spec, trackError]);

	const { mutate } = save;
	const sendRequest = useCallback(
		(body: TRow[], release: () => void) => {
			mutate(body, {
				// Re-seed from what the server STORED ("all" collapsed to [], fresh ids), through the same
				// conversion as the initial load, so the draft agrees with `savedRows` and is clean again.
				onSuccess: ({ configs }) => reseed(spec.fromApi(configs, roles)),
				onSettled: release,
			});
		},
		[mutate, reseed, roles, spec],
	);
	const sendSave = useSingleFlightSave(sendRequest);
	const requestSave = useCallback(() => {
		const body = buildBody();
		if (body !== undefined) sendSave(body);
	}, [buildBody, sendSave]);

	const discard = useCallback(() => reseed(savedRows), [reseed, savedRows]);

	return { rows, setRows, roles, jobIds, departmentId, rowsLoaded, isDirty, reasons, discard, requestSave, isPending: save.isPending };
};
